<template>
    <b-container>
        <h4>Annual Plan List</h4>
        <h6>Click a column header to sort the table.</h6>
        <div class="section-line" />
        <b-form-row>
            <b-col md="5" class="my-2">
                <b-input-group prepend="Filter">
                    <b-form-input v-model="filter" placeholder="Type to filter the table." />
                    <b-input-group-append>
                        <b-button :disabled="!filter" @click="filter = ''">
                            Clear
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </b-col>
            <b-col md="1" />
            <b-col md="5" class="my-2">
                <ay-select v-model="aySelected" :ay-default="isReviewer ? currentAy : null" />
            </b-col>
        </b-form-row>
        <applicationListTable
            id="application-list-table"
            :items="applicationListItems"
            :columns="applicationListFields"
            :actions="actionItems"
            :filter="filter"
            :ay-select="aySelected"
            @row-clicked="selectApplication"
            @print-clicked="printAnnualPlan"
        />
        <div v-if="canCreateApplication">
            <b-button variant="primary" @click="newApplication">
                {{ createApplicationButtonText }}
            </b-button>
        </div>
    </b-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
    name: 'ApplicationList',

    metaInfo() {
        return { title: 'Annual Local Plans' };
    },

    data() {
        return {
            application_type: null,
            appName: process.env.VUE_APP_CLIENT_NAME,
            formDisabled: false,
            filter: null,
            aySelected: null,
            actionItems: [
                {
                    name: 'print',
                    icon: ['fal', 'print'],
                    iconVariant: 'outline-mono-2',
                    tooltip: 'Printing this annual plan',
                },
            ],
        };
    },

    computed: {
        ...mapGetters({
            activeRealm: 'auth/activeRealm',
            isAuthorized: 'auth/isAuthorized',
            isReviewer: 'auth/isReviewer',
            applicantName: 'ref/ee/ficeName',
            applicationList: 'application/applicationList',
            currentAy: 'ref/global/currentAy',
            applicationId: 'application/applicationId'
        }),

        ...mapState('localApplication', {
            localApplicationList: 'list'
        }),

        applicationListFields() {
            return [
                { key: 'academic_year', label: 'AY', sortable: true },
                {
                    key: 'application_id',
                    label: 'Plan ID',
                    sortable: true,
                },
                { key: 'applicant_name', label: 'Institution', sortable: true },
                { key: '_actions', label: 'Actions', sortable: false },
            ];
        },

        applicationListItems() {
            return this.applicationList.map((app) => {
                app.applicant_name = this.applicantName(app.applicant_id);
                return app;
            });
        },

        canCreateApplication() {
            return (
                this.isAuthorized('create', 'application') &&
                !this.hasApplicationForCurrentAy &&
                this.hasApprovedLocalApplicationForCurrentAy
            );
        },

        hasApplicationForCurrentAy() {
            return this.applicationList.some((app) => app.academic_year === this.currentAy);
        },

        createApplicationButtonText() {
            return 'Create Annual Plan (AY' + this.currentAy + ')';
        },

        hasApprovedLocalApplicationForCurrentAy() {
            return this.localApplicationList.some((lapp) => {
                return lapp.academicYear === this.currentAy &&
                    lapp.state.currentState === 'APPROVED';
            });
        },

    },

    created() {
        this.$store.dispatch('ref/ee/fetchRefEe', this.currentAy);
        this.$store.dispatch('application/fetchApplicationList');
        this.$store.dispatch('localApplication/fetchList'); // used for canCreateApplication()
    },

    methods: {
        selectApplication(item) {
            this.$router.push({
                // name: item.application_type + '.Application',
                name: 'annualPlan',
                params: { applicationId: item.application_id },
            });
        },

        printAnnualPlan(row) {
            const url = process.env.VUE_APP_API_URL + 'print/applications/' + row.application_id;
            window.open(url, '_blank');
        },

        async newApplication() {
            if (this.activeRealm) {
                await this.$store.dispatch('application/createApplication', { application_type: this.activeRealm });
                await this.$store.dispatch('coverpage/save', this.applicationId);

                this.$router.push({
                    name: 'annualPlan',
                    params: {
                        formDisabled: false,
                        applicationId: this.applicationId
                    },
                });
            }
        },
    },
};
</script>

<style lang="scss">
h4 {
    text-align: center;
}
h6 {
    text-align: center;
}
td.table-success {
    vertical-align: middle;
    text-align: center;
}
table#list-of-applications {
    vertical-align: middle;
    text-align: center;
}
tr {
    cursor: pointer;
}
</style>
